/**
 * EditDrawer — side panel for viewing/editing one record (customer, template, …).
 * Replaces the old centered Bootstrap modals.
 *
 * Slides in from the LEFT (the menu is on the right in RTL), keeps the list visible
 * behind it, and has a fixed footer with the save/cancel buttons.
 *
 * Unsaved-changes guard: when `dirty` is true, closing (✕, Esc, click outside or the
 * cancel button via `requestClose`) asks for confirmation first.
 *
 * Props:
 *   open       bool
 *   title      node
 *   subtitle   node      small gray line under the title
 *   onClose    fn        called when the drawer should close (after the dirty check) —
 *                        the parent sets open=false
 *   afterClose fn        called once the close animation finished (unmount the parent here)
 *   dirty      bool      form has unsaved changes
 *   footer     fn(requestClose) → node   buttons; call requestClose() for "cancel"
 *   width      number    default 600 (full width on phones)
 *   extra      node      header content opposite the ✕ (e.g. a <RowActions mode="menu"> "⋯" menu)
 *   children   node      usually <Section>s / <FormGrid>
 *
 * Usage:
 *   <EditDrawer open title="עדכון לקוח" dirty={isDirty} onClose={close}
 *     footer={(requestClose) => <><Button onClick={requestClose}>ביטול</Button><Button type="primary">שמור</Button></>}>
 *     …
 *   </EditDrawer>
 */
import React, { useEffect, useRef } from 'react';
import { Drawer, Modal, Grid } from 'antd';
import styles from './EditDrawer.css';

const EditDrawer = ({ open, title, subtitle, onClose, afterClose, dirty, footer, extra, width = 600, children }) => {
  const screens = Grid.useBreakpoint();
  const confirmRef = useRef(null);

  // If the drawer closes by other means (e.g. after saving) while the "close without saving?"
  // prompt is up, remove the prompt — otherwise it lingers as an invisible layer that blocks clicks.
  const destroyConfirm = () => {
    if (confirmRef.current) {
      confirmRef.current.destroy();
      confirmRef.current = null;
    }
  };
  useEffect(() => {
    if (!open) destroyConfirm();
  }, [open]);
  useEffect(() => destroyConfirm, []);

  const requestClose = (e) => {
    // Esc pressed to close a dropdown / date picker inside the drawer must not close the drawer.
    if (e && e.type === 'keydown' && e.target && e.target.closest && e.target.closest('.ant-select, .ant-picker, .ant-dropdown')) {
      return;
    }
    if (!dirty) return onClose();
    if (confirmRef.current) return; // already asking
    confirmRef.current = Modal.confirm({
      title: 'לסגור בלי לשמור?',
      content: 'השינויים שעשית לא יישמרו.',
      okText: 'סגור בלי לשמור',
      okButtonProps: { danger: true },
      cancelText: 'המשך לערוך',
      onOk: () => {
        confirmRef.current = null;
        onClose();
      },
      onCancel: () => {
        confirmRef.current = null;
      },
    });
  };

  return (
    <Drawer
      open={open}
      placement="left"
      width={screens.md ? width : '100%'}
      onClose={requestClose}
      afterOpenChange={(isOpen) => {
        if (!isOpen && afterClose) afterClose();
      }}
      title={
        <div className={styles.titleBlock}>
          <div className={styles.title}>{title}</div>
          {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
        </div>
      }
      extra={extra}
      footer={footer ? <div className={styles.footer}>{footer(requestClose)}</div> : null}
      className={styles.drawer}
      styles={{ body: { background: 'var(--ui-page-bg)', padding: 16 } }}
    >
      <div className={styles.body}>{children}</div>
    </Drawer>
  );
};

export default EditDrawer;
